/**
 * ATTEST-UI-1 screens 5 + 6 — disclaimer confirmation and the DRAFT letter.
 *
 * The disclaimers rendered here are the composed set the engine put in the
 * letter (policy-mandatory plus type-required), read back off the preview
 * response rather than assembled locally. That matters: the CPA confirms the
 * sentences that will actually be in the document, not a UI paraphrase of them.
 * Download stays disabled until every one is ticked — the brief's "cannot
 * finalize without them", enforced on the control itself so the Playwright
 * assertion is on the gate and not on a toast.
 *
 * The DRAFT banner is the engine's (`is_draft = status != "live"`). It is
 * mirrored here so the state is visible before the .docx is opened. Removing it
 * waits on the type's legal review and is explicitly not this brief.
 *
 * Its treatment is informational amber (the same `amber-300/50/900` the sibling
 * IndependenceStep uses), not red. Draft is a normal, expected state of a letter
 * type whose legal review is still open — not a failure the CPA caused or can
 * clear. Red is reserved on these screens for refusals and gate failures, which
 * are actionable; styling a standing condition the same way trains people to
 * read real errors as decoration. The status Badge is `secondary` for the same
 * reason. The wording is unchanged and still says "must not be issued to a third
 * party" — the constraint is carried by the sentence, not by the colour.
 */
import type { AttestRefusal, LetterArtifact } from '@/components/attest/api'
import { trailRows } from '@/components/attest/evidenceTrail'
import { PreflightChecklist } from '@/components/attest/PreflightChecklist'
import { RefusalPanel } from '@/components/attest/RefusalPanel'
import {
  refusalAnchor,
  type AttestStepKey,
} from '@/components/attest/refusalGuidance'
import {
  missingRequiredFields,
  type RequiredFieldContext,
} from '@/components/attest/requiredFields'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

export interface LetterPreviewStepProps {
  artifact: LetterArtifact | null
  refusal: AttestRefusal | null
  busy: boolean
  downloading: boolean
  confirmed: Record<number, boolean>
  onToggleDisclaimer: (index: number, value: boolean) => void
  onPreview: () => void
  onDownload: () => void
  /**
   * Preflight inputs (ATTEST-INDEPENDENCE-MATRIX-1 G.3). This is the whole
   * required-field context rather than three of its members: the preflight now
   * lists the same empty fields the earlier steps starred, and those include
   * step-1 answers (ATTEST-UX-1 D1).
   */
  preflight: RequiredFieldContext
  onGoToStep?: (step: AttestStepKey, fieldAnchor?: string) => void
}

export function LetterPreviewStep({
  artifact, refusal, busy, downloading, confirmed,
  onToggleDisclaimer, onPreview, onDownload,
  preflight, onGoToStep,
}: LetterPreviewStepProps) {
  const disclaimers = artifact?.disclaimers ?? []
  const allConfirmed = disclaimers.length > 0 && disclaimers.every((_, i) => confirmed[i])
  const trail = trailRows(artifact?.evidence_index)

  return (
    <div className="space-y-5">
      <PreflightChecklist {...preflight} onGoToStep={onGoToStep} />

      <div className="flex flex-wrap gap-3">
        <Button onClick={onPreview} disabled={busy} data-testid="preview-letter">
          {busy ? 'Building…' : artifact ? 'Rebuild preview' : 'Build the letter'}
        </Button>
        {artifact && (
          <Button
            variant="outline"
            onClick={onDownload}
            disabled={!allConfirmed || downloading}
            data-testid="download-docx"
          >
            {downloading ? 'Preparing…' : 'Download .docx'}
          </Button>
        )}
      </div>

      {refusal && (
        <RefusalPanel
          refusal={refusal}
          onGoToStep={onGoToStep}
          // Resolved here rather than inside the panel: which field a
          // "something is missing" refusal is about depends on what the form
          // currently holds, and this is the component that holds it.
          fieldAnchor={refusalAnchor(
            refusal.error_code,
            missingRequiredFields(preflight).map(e => e.id),
          )}
        />
      )}

      {artifact && (
        <>
          {artifact.is_draft && (
            <div
              data-testid="draft-banner"
              className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
            >
              <p className="text-sm font-bold uppercase tracking-widest text-amber-900">Draft</p>
              <p className="mt-1 text-sm text-amber-900">
                This letter type is <span className="font-mono">{artifact.status}</span> and its
                independent legal review is not complete. The document carries a DRAFT banner
                and must not be issued to a third party.
              </p>
            </div>
          )}

          <section className="rounded-lg border border-border p-4">
            <header className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold">{artifact.display_name}</h3>
                <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                  {artifact.letter_type} v{artifact.letter_type_version} ·{' '}
                  {artifact.engagement_type} · policy {artifact.policy_version}
                </p>
              </div>
              <Badge variant={artifact.is_draft ? 'secondary' : 'default'}>
                {artifact.status}
              </Badge>
            </header>

            <div className="mt-4 space-y-3 text-sm text-gray-800" data-testid="letter-body">
              {artifact.body_paragraphs.map((p, i) => <p key={i}>{p}</p>)}

              {artifact.statement_lines.length > 0 && (
                <>
                  <p className="pt-2 font-semibold">{artifact.statements_heading}</p>
                  <ul className="list-disc space-y-1 pl-5">
                    {artifact.statement_lines.map((line, i) => <li key={i}>{line}</li>)}
                  </ul>
                </>
              )}

              {artifact.closing_paragraphs.map((p, i) => <p key={`c-${i}`}>{p}</p>)}

              {artifact.independence_disclosure && (
                <p className="text-gray-700">{artifact.independence_disclosure}</p>
              )}

              {artifact.restricted_use?.text && (
                <div className="rounded border border-border bg-muted/40 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide">
                    {artifact.restricted_use.heading}
                  </p>
                  <p className="mt-1">{artifact.restricted_use.text}</p>
                </div>
              )}
            </div>
          </section>

          <section className="rounded-lg border border-border p-4">
            <h3 className="text-sm font-semibold">Confirm the disclaimers</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              These appear verbatim in the letter. The .docx cannot be downloaded until each
              is confirmed.
            </p>
            <ul className="mt-3 space-y-3" data-testid="disclaimers">
              {disclaimers.map((text, i) => (
                <li key={i}>
                  <label className="flex items-start gap-3 text-sm" htmlFor={`disclaimer-${i}`}>
                    <input
                      id={`disclaimer-${i}`}
                      data-testid={`disclaimer-${i}`}
                      type="checkbox"
                      className="mt-0.5 h-4 w-4"
                      checked={Boolean(confirmed[i])}
                      onChange={e => onToggleDisclaimer(i, e.target.checked)}
                    />
                    <span className="text-gray-800">{text}</span>
                  </label>
                </li>
              ))}
            </ul>
            {!allConfirmed && (
              <p className="mt-3 text-sm text-amber-800" data-testid="disclaimer-gate-notice">
                Confirm every disclaimer to enable the download.
              </p>
            )}
          </section>

          {/* Bundle 2 item 4. Named fields, not `Object.entries` — two of the
              row's keys hold objects, and stringifying them printed
              `evidence=[object Object]` into the CPA's audit trail. The shape
              and the reasoning live in `evidenceTrail.ts`; every verdict and
              every sentence here is still the server's. */}
          {trail.length > 0 && (
            <section className="rounded-lg border border-border p-4" data-testid="evidence-trail">
              <h3 className="text-sm font-semibold">Evidence trail</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                What this letter relied on, statement by statement.
              </p>
              <ol className="mt-3 space-y-3">
                {trail.map((row, i) => (
                  <li
                    key={i}
                    className="rounded-md bg-muted/40 p-3"
                    data-testid={`evidence-trail-${i}`}
                  >
                    <p className="text-sm text-gray-800">{row.statement}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      <span className="font-mono">{row.assertionId}</span>
                      {' · '}
                      {row.verified ? (
                        <span className="font-medium text-emerald-700">Verified</span>
                      ) : (
                        <span className="font-medium text-amber-800">
                          Needs review — <span className="font-mono">{row.code}</span>
                        </span>
                      )}
                    </p>

                    {row.sources.length > 0 ? (
                      <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                        {row.sources.map((s, j) => (
                          <li key={j}>
                            <span className="font-mono text-foreground">{s.citation}</span>
                            {s.kind && (
                              <> · {s.kind}{s.derivation ? ` (${s.derivation})` : ''}</>
                            )}
                            {s.figure && <> · <span className="font-mono">{s.figure}</span></>}
                            {s.facts && <> · {s.facts}</>}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      // Stated as the absence it is. Why there is no document —
                      // an attestation, or an assertion that never needed one —
                      // is not something this response says, and guessing it
                      // would put a claim about the signer's file on screen.
                      <p className="mt-2 text-xs text-muted-foreground">
                        No document attached to this statement.
                      </p>
                    )}

                    {row.notes.map((n, j) => (
                      <p key={j} className="mt-2 text-xs text-amber-800">
                        <span className="font-medium">{n.gate}</span>: {n.reason}
                      </p>
                    ))}
                  </li>
                ))}
              </ol>
            </section>
          )}
        </>
      )}
    </div>
  )
}
